import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'remeda'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  addToCartStatusChangeMilliseconds,
  dataTestIds,
} from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { handleError } from '@velasca/utils/handle-error'
import * as S from './QuickAddDropdown.styles'
import type { QuickAddDropdownProps } from './QuickAddDropdown.types'

export const QuickAddDropdown = ({
  sizes,
  handle,
  onAddToCart,
  shouldOpenCartDrawerOnAddToCart,
  condensedLayout = false,
  stickToAddedToCart = false,
  buttonLabelOverride,
  productListRef,
}: QuickAddDropdownProps) => {
  const { t } = useTranslation('product')
  const [sizesState, setSizesState] = useState(sizes)
  const [quickAddState, setQuickAddState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle')
  const { addToCart, setIsDrawerOpen } = useCart()

  const decreaseQuantity = useCallback((id: string) => {
    setSizesState((prevSizes) =>
      prevSizes?.map((size) =>
        size.id === id
          ? { ...size, quantityAvailable: size.quantityAvailable - 1 }
          : size,
      ),
    )
  }, [])

  const handleAddToCart = useCallback(
    (id: string) => {
      setQuickAddState('loading')
      addToCart(id, 1)
        .then(() => {
          setQuickAddState('success')
          decreaseQuantity(id)
          setTimeout(() => {
            window.location.pathname !== '/cart' &&
              shouldOpenCartDrawerOnAddToCart &&
              setIsDrawerOpen(true)
          }, addToCartStatusChangeMilliseconds)

          // If not sticky, revert back to idle after the success delay
          if (!stickToAddedToCart) {
            setTimeout(() => {
              setQuickAddState('idle')
            }, addToCartStatusChangeMilliseconds)
          }
        })
        .catch((error) => {
          setQuickAddState('error')
          handleError(error, { message: "Couldn't add product to cart." })

          // Always revert to idle after showing error state
          setTimeout(() => {
            setQuickAddState('idle')
          }, addToCartStatusChangeMilliseconds)
        })
        .finally(() => {
          onAddToCart?.(handle)
        })
    },
    [
      addToCart,
      setIsDrawerOpen,
      decreaseQuantity,
      shouldOpenCartDrawerOnAddToCart,
      onAddToCart,
      handle,
      stickToAddedToCart,
    ],
  )

  // Keep UI in sync with external cart state for sticky success behavior
  const prevStickyRef = useRef(stickToAddedToCart)

  useEffect(() => {
    // When it becomes sticky, ensure we show success (and avoid redundant set if already success)
    if (stickToAddedToCart && quickAddState !== 'success') {
      setQuickAddState('success')
    }

    // When it transitions from sticky -> not sticky, revert success back to idle
    if (
      !stickToAddedToCart &&
      prevStickyRef.current &&
      quickAddState === 'success'
    ) {
      setQuickAddState('idle')
    }

    prevStickyRef.current = stickToAddedToCart
  }, [stickToAddedToCart, quickAddState])

  const buttonLabel = useMemo(() => {
    // Show success message when in success state
    if (quickAddState === 'success') {
      return t('addedToCart')
    }

    // Otherwise, if a label override was provided (e.g., wishlist size), prefer it
    if (buttonLabelOverride) {
      return buttonLabelOverride
    }

    return t('quickAdd')
  }, [buttonLabelOverride, quickAddState, t])

  const icon = useMemo(() => {
    if (quickAddState === 'loading') {
      return <S.IconLoading />
    }
    if (quickAddState === 'success') {
      return <S.IconSuccess />
    }
    if (quickAddState === 'error') {
      return <S.IconError />
    }
    if (sizesState && sizesState.length > 1) {
      return <S.IconCaret />
    }

    return <S.IconEmpty />
  }, [sizesState, quickAddState])

  if (!sizesState || isEmpty(sizesState)) {
    return null
  }

  return (
    <S.Container condensedLayout={condensedLayout}>
      {sizesState && sizesState.length > 1 ? (
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger asChild>
            <S.Button
              data-testid={dataTestIds.QUICK_ADD_TO_CART_BUTTON}
              condensedLayout={condensedLayout}
              disabled={quickAddState !== 'idle'}
              success={quickAddState === 'success'}
              tabIndex={quickAddState !== 'idle' ? -1 : 0}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  e.preventDefault()
                }
              }}
            >
              {buttonLabel}
              {icon}
            </S.Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal container={productListRef?.current}>
            <DropdownMenu.Content asChild sideOffset={0} align="end">
              <S.DropdownList>
                {sizesState.map((size) => {
                  const isOutOfStock = size.quantityAvailable === 0

                  return (
                    <DropdownMenu.Item
                      asChild
                      key={size.id}
                      disabled={quickAddState !== 'idle'}
                      onSelect={(e) => {
                        if (quickAddState !== 'idle') {
                          e.preventDefault()

                          return
                        }
                        if (!isOutOfStock) {
                          handleAddToCart(size.id)
                        }
                      }}
                    >
                      {isOutOfStock ? (
                        <S.DropdownItem isOutOfStock>
                          <a href={`/products/${handle}`}>
                            <span>{size.name}</span>
                            <span>{t('temporarilyNotAvailable')}</span>
                          </a>
                        </S.DropdownItem>
                      ) : (
                        <S.DropdownItem isOutOfStock={false}>
                          {size.name}
                        </S.DropdownItem>
                      )}
                    </DropdownMenu.Item>
                  )
                })}
              </S.DropdownList>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ) : (
        <S.Button
          data-testid={dataTestIds.QUICK_ADD_TO_CART_BUTTON}
          condensedLayout={condensedLayout}
          disabled={quickAddState !== 'idle'}
          success={quickAddState === 'success'}
          tabIndex={quickAddState !== 'idle' ? -1 : 0}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (quickAddState !== 'idle') {
              return
            }
            if (sizesState?.length === 1) {
              handleAddToCart(sizesState[0].id)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              if (quickAddState !== 'idle') {
                return
              }
              if (sizesState?.length === 1) {
                handleAddToCart(sizesState[0].id)
              }
            }
          }}
        >
          {buttonLabel}
          {icon}
        </S.Button>
      )}
    </S.Container>
  )
}
